import { useEffect, useState } from "react";
import { Sparkles, Wrench, FileWarning, ChevronDown, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Section, StatusBadge } from "@/components/common";

export const NO_PRO = "This tenant does not have Agyin Pro.";
const lbl = (s) => (s || "").replace(/_/g, " ");

const Locked = ({ testId, children }) => <span title={NO_PRO} data-testid={testId} className="inline-flex cursor-not-allowed">{children}</span>;

function Triage({ id, pro }) {
  const [t, setT] = useState(null);
  const [open, setOpen] = useState(true);
  useEffect(() => { if (pro) api.post(`/support/tickets/${id}/triage`).then((r) => setT(r.data)).catch(() => setT({ error: true })); }, [id, pro]);
  return (
    <Section testId="agyin-triage-panel">
      <button className="flex w-full items-center gap-2 px-5 py-3 text-left text-sm font-semibold text-slate-800" data-testid="agyin-triage-toggle" onClick={() => setOpen(!open)} disabled={!pro}>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}<Sparkles className="h-4 w-4 text-[#2B7F77]" />Agyin auto-triage
        <StatusBadge tone="neutral" label="advisory only" className="!normal-case" />
        {!pro && <Locked testId="agyin-triage-locked"><StatusBadge tone="neutral" label="Agyin Pro" className="!normal-case opacity-60" /></Locked>}</button>
      {open && <div className="border-t px-5 py-3 text-sm" style={{ borderColor: "var(--po-border)" }}>
        {!pro ? <p className="text-slate-400" title={NO_PRO} data-testid="agyin-triage-disabled">{NO_PRO}</p>
          : !t ? <span className="text-slate-500">Agyin is triaging this ticket…</span>
            : t.error ? <span className="text-slate-500">Triage is unavailable right now.</span>
              : <dl className="space-y-1.5" data-testid="agyin-triage-result">
                <div className="flex gap-2"><dt className="w-28 text-slate-500">Category</dt><dd data-testid="triage-category">{lbl(t.category)}{t.category !== t.current?.category && <span className="text-xs text-slate-400"> (currently {lbl(t.current?.category)})</span>}</dd></div>
                <div className="flex gap-2"><dt className="w-28 text-slate-500">Severity</dt><dd data-testid="triage-severity">{t.severity}{t.severity !== t.current?.severity && <span className="text-xs text-slate-400"> (currently {t.current?.severity})</span>}</dd></div>
                <div className="flex gap-2"><dt className="w-28 shrink-0 text-slate-500">Likely cause</dt><dd data-testid="triage-root-cause">{t.root_cause} <span className="text-xs text-slate-400">({t.confidence} confidence)</span></dd></div>
              </dl>}
      </div>}
    </Section>);
}

function Runbook({ id, onClose }) {
  const [sid] = useState(() => `rb-${Date.now().toString(36)}`);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const step = async (message = "") => {
    setBusy(true);
    if (message) setMsgs((m) => [...m, { role: "agent", text: message }]);
    try { const r = (await api.post(`/support/tickets/${id}/troubleshoot`, { session_id: sid, message })).data; setMsgs((m) => [...m, { role: "agyin", text: r.reply, kb: r.kb_used }]); }
    catch (e) { toast.error(errMsg(e)); }
    setText(""); setBusy(false);
  };
  useEffect(() => { step(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="max-w-xl" data-testid="runbook-dialog">
      <DialogHeader><DialogTitle>Troubleshoot with Agyin</DialogTitle><DialogDescription>A step-by-step runbook. Report what you find and Agyin gives the next step.</DialogDescription></DialogHeader>
      <div className="max-h-[50vh] space-y-2 overflow-y-auto" data-testid="runbook-messages">
        {msgs.map((m, i) => <div key={i} className={`rounded-lg px-3 py-2 text-sm ${m.role === "agent" ? "ml-8 bg-[#13294B] text-white" : "mr-4 border bg-white"}`} data-testid={`runbook-msg-${m.role}`}>
          <div className="whitespace-pre-wrap">{m.text}</div>{!!m.kb?.length && <div className="mt-1 text-[11px] text-slate-400">KB used: {m.kb.join(", ")}</div>}</div>)}
        {busy && <div className="text-xs text-slate-500">Agyin is thinking…</div>}
      </div>
      <div className="flex items-end gap-2"><Textarea data-testid="runbook-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="What did you find?" />
        <Button data-testid="runbook-send" disabled={busy || !text.trim()} onClick={() => step(text.trim())}><Send className="h-4 w-4" /></Button></div>
    </DialogContent></Dialog>);
}

function Escalation({ id, onClose, onSaved }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.post(`/support/tickets/${id}/escalation/draft`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); }); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => { try { await api.post(`/support/tickets/${id}/escalation/save`, { text: d.text }); toast.success("Escalation note saved"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent className="max-w-xl" data-testid="escalation-dialog">
      <DialogHeader><DialogTitle>Draft escalation note</DialogTitle><DialogDescription>Preview and edit. It is saved as an internal note only when you confirm.</DialogDescription></DialogHeader>
      {!d ? <div className="text-sm text-slate-500">Agyin is drafting…</div> : <Textarea data-testid="escalation-text" rows={12} value={d.text} onChange={(e) => setD({ ...d, text: e.target.value })} />}
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="escalation-save" disabled={!d?.text?.trim()} onClick={save} className="bg-[#13294B] hover:bg-[#1c3a66]">Confirm &amp; save note</Button></DialogFooter>
    </DialogContent></Dialog>);
}

export function OpsProTools({ id, pro, readOnly, onSaved }) {
  const [dlg, setDlg] = useState(null);
  const btn = (key, Icon, label) => {
    const b = <Button size="sm" variant="outline" data-testid={`${key}-button`} disabled={!pro || readOnly} onClick={() => setDlg(key)}><Icon className="mr-1 h-4 w-4" />{label}</Button>;
    return pro ? b : <Locked key={key} testId={`${key}-locked`}>{b}</Locked>;
  };
  return (
    <div className="space-y-3" data-testid="ops-pro-tools">
      <Triage id={id} pro={pro} />
      {!readOnly && <div className="flex flex-wrap gap-2">{btn("troubleshoot", Wrench, "Troubleshoot with Agyin")}{btn("escalation", FileWarning, "Draft escalation note")}</div>}
      {dlg === "troubleshoot" && <Runbook id={id} onClose={() => setDlg(null)} />}
      {dlg === "escalation" && <Escalation id={id} onClose={() => setDlg(null)} onSaved={onSaved} />}
    </div>);
}
